/**
 * Datos de recetas de Intercasa
 * Contiene la información de las 12 recetas según recetas_individuales.md
 * 6 recetas de 2024 y 6 de 2025 para mantener contenido actualizado
 *
 * Contenido multidioma: cada texto traducible es un objeto `{ es, en, fr, pt }`
 * y las listas (ingredientes, pasos) son `{ es: [...], en: [...], ... }` con la
 * misma longitud en todos los idiomas. Ids, slugs (las URLs siguen en español),
 * imágenes, fechas, enlaces y nombres de producto (marca) son valores simples.
 * Los componentes lo resuelven con `useLocalizeContent`; ver
 * `i18n/localizeContent.js`.
 */

export const recipesData = [
  // ==========================================
  // RECETAS CON KETCHUP INTERCASA
  // ==========================================
  {
    id: 1,
    title: {
      es: 'Pollo glaseado con ketchup y miel',
      en: 'Honey and ketchup glazed chicken',
      fr: 'Poulet glacé au ketchup et au miel',
      pt: 'Frango glaceado com ketchup e mel',
    },
    description: {
      es: 'Una receta rápida, jugosa y llena de sabor. El ketchup Intercasa aporta un toque dulce y equilibrado.',
      en: 'A quick, juicy recipe full of flavour. Intercasa ketchup adds a sweet, balanced touch.',
      fr: 'Une recette rapide, juteuse et pleine de saveur. Le ketchup Intercasa apporte une touche sucrée et équilibrée.',
      pt: 'Uma receita rápida, suculenta e cheia de sabor. O ketchup Intercasa dá um toque doce e equilibrado.',
    },
    date: '2024-02-10',
    slug: 'pollo-glaseado-ketchup-miel',
    imageUrl: '/images/recipes/pollo-glaseado.webp',
    imageAlt: {
      es: 'Pollo glaseado con ketchup y miel Intercasa',
      en: 'Honey and ketchup glazed chicken with Intercasa ketchup',
      fr: 'Poulet glacé au ketchup et au miel avec du ketchup Intercasa',
      pt: 'Frango glaceado com mel e ketchup Intercasa',
    },
    url: '/recetas/pollo-glaseado-ketchup-miel/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Una receta rápida, jugosa y llena de sabor. El ketchup Intercasa aporta un toque dulce y equilibrado, mientras que la miel carameliza el pollo de forma natural. Perfecta para comidas familiares o platos semanales fáciles.',
      en: 'A quick, juicy recipe full of flavour. Intercasa ketchup adds a sweet, balanced touch, while the honey naturally caramelises the chicken. Perfect for family meals or easy weeknight dishes.',
      fr: 'Une recette rapide, juteuse et pleine de saveur. Le ketchup Intercasa apporte une touche sucrée et équilibrée, tandis que le miel caramélise naturellement le poulet. Parfaite pour les repas en famille ou les plats faciles de la semaine.',
      pt: 'Uma receita rápida, suculenta e cheia de sabor. O ketchup Intercasa dá um toque doce e equilibrado, enquanto o mel carameliza o frango de forma natural. Perfeita para refeições em família ou para pratos fáceis do dia a dia.',
    },
    ingredients: {
      es: [
        '500 g de pollo en cubos',
        '4 cdas de ketchup Intercasa',
        '2 cdas de miel',
        '1 cda de salsa de soja',
        '1 diente de ajo rallado',
        'Sal y pimienta',
      ],
      en: [
        '500 g diced chicken',
        '4 tbsp Intercasa ketchup',
        '2 tbsp honey',
        '1 tbsp soy sauce',
        '1 clove of garlic, grated',
        'Salt and pepper',
      ],
      fr: [
        '500 g de poulet en dés',
        '4 c. à s. de ketchup Intercasa',
        '2 c. à s. de miel',
        '1 c. à s. de sauce soja',
        '1 gousse d’ail râpée',
        'Sel et poivre',
      ],
      pt: [
        '500 g de frango em cubos',
        '4 colheres de sopa de ketchup Intercasa',
        '2 colheres de sopa de mel',
        '1 colher de sopa de molho de soja',
        '1 dente de alho ralado',
        'Sal e pimenta',
      ],
    },
    instructions: {
      es: [
        'Mezclar ketchup, miel, soja y ajo.',
        'Marinar el pollo 20-30 minutos.',
        'Cocinar en sartén hasta dorar.',
        'Añadir el resto de la marinada y dejar espesar.',
        'Una vez alcanzado el espesor deseado, espolvorea un poco de sésamo.',
        'Servir con arroz o puré.',
      ],
      en: [
        'Mix the ketchup, honey, soy sauce and garlic.',
        'Marinate the chicken for 20–30 minutes.',
        'Cook in a frying pan until golden.',
        'Add the rest of the marinade and let it thicken.',
        'Once it has reached the desired thickness, sprinkle over a little sesame.',
        'Serve with rice or mashed potato.',
      ],
      fr: [
        'Mélanger le ketchup, le miel, la sauce soja et l’ail.',
        'Faire mariner le poulet pendant 20 à 30 minutes.',
        'Cuire à la poêle jusqu’à ce qu’il soit doré.',
        'Ajouter le reste de la marinade et laisser épaissir.',
        'Une fois l’épaisseur souhaitée atteinte, saupoudrer d’un peu de sésame.',
        'Servir avec du riz ou de la purée.',
      ],
      pt: [
        'Misturar o ketchup, o mel, o molho de soja e o alho.',
        'Deixar o frango a marinar durante 20 a 30 minutos.',
        'Cozinhar numa frigideira até ficar dourado.',
        'Juntar o resto da marinada e deixar engrossar.',
        'Quando atingir a espessura desejada, polvilhar com um pouco de sésamo.',
        'Servir com arroz ou puré.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Ketchup Intercasa',
        category: {
          es: 'Ketchup',
          en: 'Ketchup',
          fr: 'Ketchup',
          pt: 'Ketchup',
        },
        link: '/productos/retail/ketchup-pe',
      },
    ],
  },
  {
    id: 2,
    title: {
      es: 'Arroz frito rojo estilo "omurice"',
      en: 'Red fried rice, “omurice” style',
      fr: 'Riz frit rouge façon « omurice »',
      pt: 'Arroz frito vermelho ao estilo «omurice»',
    },
    description: {
      es: 'Un plato casero japonés muy popular, ideal para aprovechar arroz del día anterior.',
      en: 'A very popular Japanese home-cooked dish, ideal for using up yesterday\'s rice.',
      fr: 'Un plat familial japonais très populaire, idéal pour utiliser le riz de la veille.',
      pt: 'Um prato caseiro japonês muito popular, ideal para aproveitar o arroz do dia anterior.',
    },
    date: '2024-04-18',
    slug: 'arroz-frito-rojo-omurice',
    imageUrl: '/images/recipes/arroz-omurice.webp',
    imageAlt: {
      es: 'Arroz frito rojo estilo omurice con ketchup Intercasa',
      en: 'Red fried rice, omurice style, with Intercasa ketchup',
      fr: 'Riz frit rouge façon omurice avec du ketchup Intercasa',
      pt: 'Arroz frito vermelho ao estilo omurice com ketchup Intercasa',
    },
    url: '/recetas/arroz-frito-rojo-omurice/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Un plato casero japonés muy popular, ideal para aprovechar arroz del día anterior. El ketchup Intercasa da color y un sabor dulce-salado característico.',
      en: 'A very popular Japanese home-cooked dish, ideal for using up yesterday\'s rice. Intercasa ketchup gives it its colour and a characteristic sweet-and-savoury flavour.',
      fr: 'Un plat familial japonais très populaire, idéal pour utiliser le riz de la veille. Le ketchup Intercasa lui donne sa couleur et une saveur sucrée-salée caractéristique.',
      pt: 'Um prato caseiro japonês muito popular, ideal para aproveitar o arroz do dia anterior. O ketchup Intercasa dá cor e um sabor doce e salgado característico.',
    },
    ingredients: {
      es: [
        '2 tazas de arroz cocido',
        '1/2 cebolla picada',
        '1/2 taza de zanahoria',
        '1/2 taza de jamón o pollo',
        '2-3 cdas de ketchup Intercasa',
        '1 cda de salsa de soja',
        '1 huevo (opcional)',
      ],
      en: [
        '2 cups cooked rice',
        '1/2 onion, chopped',
        '1/2 cup carrot',
        '1/2 cup ham or chicken',
        '2–3 tbsp Intercasa ketchup',
        '1 tbsp soy sauce',
        '1 egg (optional)',
      ],
      fr: [
        '2 tasses de riz cuit',
        '1/2 oignon haché',
        '1/2 tasse de carotte',
        '1/2 tasse de jambon ou de poulet',
        '2 à 3 c. à s. de ketchup Intercasa',
        '1 c. à s. de sauce soja',
        '1 œuf (facultatif)',
      ],
      pt: [
        '2 chávenas de arroz cozido',
        '1/2 cebola picada',
        '1/2 chávena de cenoura',
        '1/2 chávena de fiambre ou frango',
        '2 a 3 colheres de sopa de ketchup Intercasa',
        '1 colher de sopa de molho de soja',
        '1 ovo (opcional)',
      ],
    },
    instructions: {
      es: [
        'Saltear cebolla y zanahoria picada en cubitos.',
        'Añadir el jamón o pollo en tamaño deseado (preferiblemente en taquitos).',
        'Agregar el arroz y mezclar todo en la sartén.',
        'Incorporar el ketchup Intercasa y la soja.',
        'Servir o cubrir con tortilla.',
      ],
      en: [
        'Sauté the onion and diced carrot.',
        'Add the ham or chicken cut to the size you prefer (ideally in small cubes).',
        'Add the rice and mix everything together in the pan.',
        'Stir in the Intercasa ketchup and the soy sauce.',
        'Serve as it is or top with an omelette.',
      ],
      fr: [
        'Faire revenir l’oignon et la carotte coupée en petits dés.',
        'Ajouter le jambon ou le poulet de la taille souhaitée (de préférence en petits morceaux).',
        'Ajouter le riz et bien mélanger le tout dans la poêle.',
        'Incorporer le ketchup Intercasa et la sauce soja.',
        'Servir tel quel ou recouvrir d’une omelette.',
      ],
      pt: [
        'Refogar a cebola e a cenoura cortada em cubinhos.',
        'Juntar o fiambre ou o frango no tamanho desejado (de preferência em cubinhos).',
        'Acrescentar o arroz e envolver tudo na frigideira.',
        'Incorporar o ketchup Intercasa e o molho de soja.',
        'Servir assim ou cobrir com uma omeleta.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Ketchup Intercasa',
        category: {
          es: 'Ketchup',
          en: 'Ketchup',
          fr: 'Ketchup',
          pt: 'Ketchup',
        },
        link: '/productos/retail/ketchup-pe',
      },
    ],
  },
  {
    id: 3,
    title: {
      es: 'Albóndigas en salsa de tomate frito',
      en: 'Meatballs in fried tomato sauce',
      fr: 'Boulettes de viande en sauce tomate',
      pt: 'Almôndegas em molho de tomate frito',
    },
    description: {
      es: 'Un clásico reconfortante con una salsa sencilla pero muy sabrosa.',
      en: 'A comforting classic with a simple but very tasty sauce.',
      fr: 'Un classique réconfortant avec une sauce simple mais très savoureuse.',
      pt: 'Um clássico reconfortante com um molho simples, mas muito saboroso.',
    },
    date: '2024-06-25',
    slug: 'albondigas-salsa-tomate-frito',
    imageUrl: '/images/recipes/albondigas-tomate.webp',
    imageAlt: {
      es: 'Albóndigas en salsa de tomate frito Intercasa',
      en: 'Meatballs in Intercasa fried tomato sauce',
      fr: 'Boulettes de viande en sauce tomate Intercasa',
      pt: 'Almôndegas em molho de tomate frito Intercasa',
    },
    url: '/recetas/albondigas-salsa-tomate-frito/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Un clásico reconfortante con una salsa sencilla pero muy sabrosa. El tomate frito Intercasa aporta el punto perfecto de acidez y dulzor.',
      en: 'A comforting classic with a simple but very tasty sauce. Intercasa fried tomato sauce adds just the right balance of acidity and sweetness.',
      fr: 'Un classique réconfortant avec une sauce simple mais très savoureuse. La sauce tomate Intercasa apporte le juste équilibre d’acidité et de douceur.',
      pt: 'Um clássico reconfortante com um molho simples, mas muito saboroso. O tomate frito Intercasa dá o ponto perfeito de acidez e doçura.',
    },
    ingredients: {
      es: [
        '500 g de carne molida',
        '1 huevo',
        '1/2 taza pan rallado',
        '1/2 taza tomate frito Intercasa',
        '1/2 taza caldo',
        '1 cda de cebolla picada',
        'Orégano, sal y pimienta',
      ],
      en: [
        '500 g minced meat',
        '1 egg',
        '1/2 cup breadcrumbs',
        '1/2 cup Intercasa fried tomato sauce',
        '1/2 cup stock',
        '1 tbsp chopped onion',
        'Oregano, salt and pepper',
      ],
      fr: [
        '500 g de viande hachée',
        '1 œuf',
        '1/2 tasse de chapelure',
        '1/2 tasse de sauce tomate Intercasa',
        '1/2 tasse de bouillon',
        '1 c. à s. d’oignon haché',
        'Origan, sel et poivre',
      ],
      pt: [
        '500 g de carne picada',
        '1 ovo',
        '1/2 chávena de pão ralado',
        '1/2 chávena de tomate frito Intercasa',
        '1/2 chávena de caldo',
        '1 colher de sopa de cebola picada',
        'Orégão, sal e pimenta',
      ],
    },
    instructions: {
      es: [
        'Mezclar la carne con huevo y pan rallado.',
        'Formar albóndigas y dorarlas.',
        'Preparar salsa con tomate frito, caldo y especias.',
        'Añadir las albóndigas a la salsa.',
        'Cocinar 20 minutos.',
      ],
      en: [
        'Mix the meat with the egg and breadcrumbs.',
        'Shape into meatballs and brown them.',
        'Make a sauce with the fried tomato sauce, stock and seasonings.',
        'Add the meatballs to the sauce.',
        'Cook for 20 minutes.',
      ],
      fr: [
        'Mélanger la viande avec l’œuf et la chapelure.',
        'Former des boulettes et les faire dorer.',
        'Préparer une sauce avec la sauce tomate, le bouillon et les épices.',
        'Ajouter les boulettes à la sauce.',
        'Laisser cuire 20 minutes.',
      ],
      pt: [
        'Misturar a carne com o ovo e o pão ralado.',
        'Formar as almôndegas e dourá-las.',
        'Preparar um molho com o tomate frito, o caldo e as especiarias.',
        'Juntar as almôndegas ao molho.',
        'Cozinhar durante 20 minutos.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Tomate Frito Intercasa',
        category: {
          es: 'Tomates en Conserva',
          en: 'Canned Tomatoes',
          fr: 'Tomates en conserve',
          pt: 'Tomates em conserva',
        },
        link: '/productos/retail/tomate-frito',
      },
    ],
  },

  // ==========================================
  // RECETAS CON SALSA BARBACOA INTERCASA
  // ==========================================
  {
    id: 4,
    title: {
      es: 'Costillas BBQ al horno',
      en: 'Oven-baked BBQ ribs',
      fr: 'Travers de porc barbecue au four',
      pt: 'Costelinhas BBQ no forno',
    },
    description: {
      es: 'Una receta irresistible, tierna y caramelizada. Ideal para reuniones o fines de semana.',
      en: 'An irresistible, tender, caramelised recipe. Ideal for get-togethers or weekends.',
      fr: 'Une recette irrésistible, tendre et caramélisée. Idéale pour les réunions entre amis ou les week-ends.',
      pt: 'Uma receita irresistível, tenra e caramelizada. Ideal para convívios ou fins de semana.',
    },
    date: '2024-08-12',
    slug: 'costillas-bbq-al-horno',
    imageUrl: '/images/recipes/costillas-bbq.webp',
    imageAlt: {
      es: 'Costillas BBQ al horno con salsa barbacoa Intercasa',
      en: 'Oven-baked BBQ ribs with Intercasa barbecue sauce',
      fr: 'Travers de porc barbecue au four avec de la sauce barbecue Intercasa',
      pt: 'Costelinhas BBQ no forno com molho barbecue Intercasa',
    },
    url: '/recetas/costillas-bbq-al-horno/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Una receta irresistible, tierna y caramelizada. La barbacoa Intercasa aporta sabor ahumado y dulzor natural, ideal para reuniones o fines de semana.',
      en: 'An irresistible, tender, caramelised recipe. Intercasa barbecue sauce adds a smoky flavour and natural sweetness, ideal for get-togethers or weekends.',
      fr: 'Une recette irrésistible, tendre et caramélisée. La sauce barbecue Intercasa apporte une saveur fumée et une douceur naturelle, idéale pour les réunions entre amis ou les week-ends.',
      pt: 'Uma receita irresistível, tenra e caramelizada. O molho barbecue Intercasa dá um sabor fumado e uma doçura natural, ideal para convívios ou fins de semana.',
    },
    ingredients: {
      es: [
        '1 kg de costillas',
        'Sal, pimienta y ajo (o especias deseadas)',
        '1/2 taza barbacoa Intercasa',
        '2 cdas miel',
      ],
      en: [
        '1 kg ribs',
        'Salt, pepper and garlic (or your preferred spices)',
        '1/2 cup Intercasa barbecue sauce',
        '2 tbsp honey',
      ],
      fr: [
        '1 kg de travers de porc',
        'Sel, poivre et ail (ou les épices de votre choix)',
        '1/2 tasse de sauce barbecue Intercasa',
        '2 c. à s. de miel',
      ],
      pt: [
        '1 kg de costelinhas',
        'Sal, pimenta e alho (ou as especiarias que preferir)',
        '1/2 chávena de molho barbecue Intercasa',
        '2 colheres de sopa de mel',
      ],
    },
    instructions: {
      es: [
        'Sazonar bien las costillas por todos los lados.',
        'Hornear 1 hora y 30 mins envueltas en papel de aluminio a 160°C (comprobando cada cierto tiempo hasta que la carne se ablande).',
        'Mezclar barbacoa Intercasa con miel.',
        'Glasear y hornear 10-20 min más.',
        'Servir caliente.',
      ],
      en: [
        'Season the ribs well on all sides.',
        'Bake for 1 hour 30 minutes wrapped in aluminium foil at 160°C (checking from time to time until the meat is tender).',
        'Mix the Intercasa barbecue sauce with the honey.',
        'Glaze and bake for a further 10–20 minutes.',
        'Serve hot.',
      ],
      fr: [
        'Assaisonner généreusement les travers sur toutes les faces.',
        'Cuire au four 1 h 30 enveloppés dans du papier aluminium à 160 °C (en vérifiant de temps en temps jusqu’à ce que la viande soit tendre).',
        'Mélanger la sauce barbecue Intercasa avec le miel.',
        'Napper et enfourner encore 10 à 20 minutes.',
        'Servir chaud.',
      ],
      pt: [
        'Temperar bem as costelinhas por todos os lados.',
        'Assar durante 1 hora e 30 minutos embrulhadas em papel de alumínio a 160°C (verificando de vez em quando até a carne ficar tenra).',
        'Misturar o molho barbecue Intercasa com o mel.',
        'Pincelar e assar mais 10 a 20 minutos.',
        'Servir quente.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Salsa Barbacoa Intercasa',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/salsa-barbacoa-pe',
      },
    ],
  },
  {
    id: 5,
    title: {
      es: 'Hamburguesa BBQ con cebolla crujiente',
      en: 'BBQ burger with crispy onions',
      fr: 'Burger barbecue aux oignons croustillants',
      pt: 'Hambúrguer BBQ com cebola crocante',
    },
    description: {
      es: 'Una hamburguesa jugosa con un toque dulce y ahumado gracias a la barbacoa Intercasa.',
      en: 'A juicy burger with a sweet, smoky touch thanks to Intercasa barbecue sauce.',
      fr: 'Un burger juteux avec une touche sucrée et fumée grâce à la sauce barbecue Intercasa.',
      pt: 'Um hambúrguer suculento com um toque doce e fumado graças ao molho barbecue Intercasa.',
    },
    date: '2024-10-20',
    slug: 'hamburguesa-bbq-cebolla-crujiente',
    imageUrl: '/images/recipes/hamburguesa-bbq.webp',
    imageAlt: {
      es: 'Hamburguesa BBQ con cebolla crujiente y salsa Intercasa',
      en: 'BBQ burger with crispy onions and Intercasa sauce',
      fr: 'Burger barbecue aux oignons croustillants et sauce Intercasa',
      pt: 'Hambúrguer BBQ com cebola crocante e molho Intercasa',
    },
    url: '/recetas/hamburguesa-bbq-cebolla-crujiente/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Una hamburguesa jugosa con un toque dulce y ahumado gracias a la barbacoa Intercasa, perfecta para comidas rápidas pero sabrosas.',
      en: 'A juicy burger with a sweet, smoky touch thanks to Intercasa barbecue sauce, perfect for quick yet tasty meals.',
      fr: 'Un burger juteux avec une touche sucrée et fumée grâce à la sauce barbecue Intercasa, parfait pour des repas rapides mais savoureux.',
      pt: 'Um hambúrguer suculento com um toque doce e fumado graças ao molho barbecue Intercasa, perfeito para refeições rápidas, mas saborosas.',
    },
    ingredients: {
      es: [
        'Hamburguesa de carne',
        'Queso cheddar',
        'Salsa barbacoa Intercasa',
        'Cebolla crujiente',
        'Pan de hamburguesa',
        'Lechuga (opcional)',
        'Tomate (opcional)',
      ],
      en: [
        'Beef burger patty',
        'Cheddar cheese',
        'Intercasa barbecue sauce',
        'Crispy fried onions',
        'Burger bun',
        'Lettuce (optional)',
        'Tomato (optional)',
      ],
      fr: [
        'Steak haché',
        'Fromage cheddar',
        'Sauce barbecue Intercasa',
        'Oignons croustillants',
        'Pain à burger',
        'Laitue (facultative)',
        'Tomate (facultative)',
      ],
      pt: [
        'Hambúrguer de carne',
        'Queijo cheddar',
        'Molho barbecue Intercasa',
        'Cebola crocante',
        'Pão de hambúrguer',
        'Alface (opcional)',
        'Tomate (opcional)',
      ],
    },
    instructions: {
      es: [
        'Cocinar la carne y colocar el queso encima.',
        'Untar barbacoa, ketchup y mostaza Intercasa en el pan.',
        'Colocar la carne en el pan, junto con una o dos hojas de lechuga y una rodaja de tomate.',
        'Añadir cebolla crujiente.',
        'Servir.',
      ],
      en: [
        'Cook the patty and place the cheese on top.',
        'Spread Intercasa barbecue sauce, ketchup and mustard on the bun.',
        'Place the patty in the bun with one or two lettuce leaves and a slice of tomato.',
        'Add the crispy onions.',
        'Serve.',
      ],
      fr: [
        'Cuire le steak haché et déposer le fromage par-dessus.',
        'Tartiner le pain de sauce barbecue, de ketchup et de moutarde Intercasa.',
        'Placer le steak dans le pain avec une ou deux feuilles de laitue et une rondelle de tomate.',
        'Ajouter les oignons croustillants.',
        'Servir.',
      ],
      pt: [
        'Cozinhar a carne e colocar o queijo por cima.',
        'Untar o pão com molho barbecue, ketchup e mostarda Intercasa.',
        'Colocar a carne no pão, juntamente com uma ou duas folhas de alface e uma rodela de tomate.',
        'Acrescentar a cebola crocante.',
        'Servir.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Salsa Barbacoa Intercasa',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/salsa-barbacoa-pe',
      },
      {
        name: 'Ketchup Intercasa',
        category: {
          es: 'Ketchup',
          en: 'Ketchup',
          fr: 'Ketchup',
          pt: 'Ketchup',
        },
        link: '/productos/retail/ketchup-pe',
      },
      {
        name: 'Mostaza Dulce Intercasa',
        category: {
          es: 'Mostaza',
          en: 'Mustard',
          fr: 'Moutarde',
          pt: 'Mostarda',
        },
        link: '/productos/retail/mostaza-dulce-pe',
      },
    ],
  },
  {
    id: 6,
    title: {
      es: 'Tiras de pollo BBQ',
      en: 'BBQ chicken strips',
      fr: 'Lanières de poulet barbecue',
      pt: 'Tiras de frango BBQ',
    },
    description: {
      es: 'Una opción fácil y deliciosa para almuerzos rápidos. La barbacoa Intercasa aporta sabor intenso.',
      en: 'An easy and delicious option for quick lunches. Intercasa barbecue sauce adds intense flavour.',
      fr: 'Une option facile et délicieuse pour les déjeuners rapides. La sauce barbecue Intercasa apporte une saveur intense.',
      pt: 'Uma opção fácil e deliciosa para almoços rápidos. O molho barbecue Intercasa dá um sabor intenso.',
    },
    date: '2024-12-05',
    slug: 'tiras-de-pollo-bbq',
    imageUrl: '/images/recipes/tiras-pollo-bbq.webp',
    imageAlt: {
      es: 'Tiras de pollo BBQ con salsa barbacoa Intercasa',
      en: 'BBQ chicken strips with Intercasa barbecue sauce',
      fr: 'Lanières de poulet barbecue avec de la sauce barbecue Intercasa',
      pt: 'Tiras de frango BBQ com molho barbecue Intercasa',
    },
    url: '/recetas/tiras-de-pollo-bbq/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Una opción fácil y deliciosa para almuerzos rápidos. La barbacoa Intercasa aporta sabor intenso en pocos minutos.',
      en: 'An easy and delicious option for quick lunches. Intercasa barbecue sauce adds intense flavour in just a few minutes.',
      fr: 'Une option facile et délicieuse pour les déjeuners rapides. La sauce barbecue Intercasa apporte une saveur intense en quelques minutes.',
      pt: 'Uma opção fácil e deliciosa para almoços rápidos. O molho barbecue Intercasa dá um sabor intenso em poucos minutos.',
    },
    ingredients: {
      es: [
        '400 g pechuga en tiras',
        'Sal y pimienta',
        '3 cdas barbacoa Intercasa',
        '1 cda miel',
      ],
      en: [
        '400 g chicken breast, cut into strips',
        'Salt and pepper',
        '3 tbsp Intercasa barbecue sauce',
        '1 tbsp honey',
      ],
      fr: [
        '400 g de blanc de poulet en lanières',
        'Sel et poivre',
        '3 c. à s. de sauce barbecue Intercasa',
        '1 c. à s. de miel',
      ],
      pt: [
        '400 g de peito de frango em tiras',
        'Sal e pimenta',
        '3 colheres de sopa de molho barbecue Intercasa',
        '1 colher de sopa de mel',
      ],
    },
    instructions: {
      es: [
        'Sazonar el pollo y dorarlo.',
        'Mezclar barbacoa Intercasa con miel.',
        'Añadir al pollo.',
        'Cocinar hasta glasear.',
        'Servir con papas o ensalada.',
      ],
      en: [
        'Season the chicken and brown it.',
        'Mix the Intercasa barbecue sauce with the honey.',
        'Add to the chicken.',
        'Cook until glazed.',
        'Serve with potatoes or salad.',
      ],
      fr: [
        'Assaisonner le poulet et le faire dorer.',
        'Mélanger la sauce barbecue Intercasa avec le miel.',
        'Ajouter au poulet.',
        'Cuire jusqu’à ce que le poulet soit glacé.',
        'Servir avec des pommes de terre ou de la salade.',
      ],
      pt: [
        'Temperar o frango e dourá-lo.',
        'Misturar o molho barbecue Intercasa com o mel.',
        'Juntar ao frango.',
        'Cozinhar até ficar glaceado.',
        'Servir com batatas ou salada.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Salsa Barbacoa Intercasa',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/salsa-barbacoa-pe',
      },
    ],
  },

  // ==========================================
  // RECETAS CON MOSTAZA INTERCASA
  // ==========================================
  {
    id: 7,
    title: {
      es: 'Ensalada de papa cremosa con mostaza',
      en: 'Creamy potato salad with mustard',
      fr: 'Salade de pommes de terre crémeuse à la moutarde',
      pt: 'Salada de batata cremosa com mostarda',
    },
    description: {
      es: 'Una guarnición fresca y cremosa, perfecta para acompañar carnes y parrilladas.',
      en: 'A fresh, creamy side dish, perfect to accompany meat and barbecues.',
      fr: 'Un accompagnement frais et crémeux, parfait avec les viandes et les grillades.',
      pt: 'Um acompanhamento fresco e cremoso, perfeito para carnes e grelhados.',
    },
    date: '2025-01-15',
    slug: 'ensalada-papa-cremosa-mostaza',
    imageUrl: '/images/recipes/ensalada-papa.webp',
    imageAlt: {
      es: 'Ensalada de papa cremosa con mostaza Intercasa',
      en: 'Creamy potato salad with Intercasa mustard',
      fr: 'Salade de pommes de terre crémeuse à la moutarde Intercasa',
      pt: 'Salada de batata cremosa com mostarda Intercasa',
    },
    url: '/recetas/ensalada-papa-cremosa-mostaza/',
    category: {
      es: 'Guarniciones',
      en: 'Side dishes',
      fr: 'Accompagnements',
      pt: 'Acompanhamentos',
    },
    introduction: {
      es: 'Una guarnición fresca y cremosa, perfecta para acompañar carnes y parrilladas. La mostaza Intercasa aporta el toque justo de acidez.',
      en: 'A fresh, creamy side dish, perfect to accompany meat and barbecues. Intercasa mustard adds just the right touch of tang.',
      fr: 'Un accompagnement frais et crémeux, parfait avec les viandes et les grillades. La moutarde Intercasa apporte la juste pointe d’acidité.',
      pt: 'Um acompanhamento fresco e cremoso, perfeito para carnes e grelhados. A mostarda Intercasa dá o toque certo de acidez.',
    },
    ingredients: {
      es: [
        '5 papas',
        '1/2 taza mayonesa',
        '1-2 cdas mostaza Intercasa',
        'Cebollín',
        'Sal y pimienta',
      ],
      en: [
        '5 potatoes',
        '1/2 cup mayonnaise',
        '1–2 tbsp Intercasa mustard',
        'Spring onion',
        'Salt and pepper',
      ],
      fr: [
        '5 pommes de terre',
        '1/2 tasse de mayonnaise',
        '1 à 2 c. à s. de moutarde Intercasa',
        'Ciboule',
        'Sel et poivre',
      ],
      pt: [
        '5 batatas',
        '1/2 chávena de maionese',
        '1 a 2 colheres de sopa de mostarda Intercasa',
        'Cebolinho',
        'Sal e pimenta',
      ],
    },
    instructions: {
      es: [
        'Cocer las papas.',
        'Mezclar mayonesa y mostaza Intercasa.',
        'Integrar las papas con suavidad.',
        'Añadir cebollín y espolvorear sal y pimienta al gusto.',
        'Refrigerar antes de servir.',
      ],
      en: [
        'Boil the potatoes.',
        'Mix the mayonnaise and Intercasa mustard.',
        'Gently fold in the potatoes.',
        'Add the spring onion and season with salt and pepper to taste.',
        'Chill before serving.',
      ],
      fr: [
        'Faire cuire les pommes de terre.',
        'Mélanger la mayonnaise et la moutarde Intercasa.',
        'Incorporer délicatement les pommes de terre.',
        'Ajouter la ciboule et assaisonner de sel et de poivre à votre goût.',
        'Réfrigérer avant de servir.',
      ],
      pt: [
        'Cozer as batatas.',
        'Misturar a maionese e a mostarda Intercasa.',
        'Envolver as batatas com suavidade.',
        'Acrescentar o cebolinho e temperar com sal e pimenta a gosto.',
        'Refrigerar antes de servir.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Mostaza Dulce Intercasa',
        category: {
          es: 'Mostaza',
          en: 'Mustard',
          fr: 'Moutarde',
          pt: 'Mostarda',
        },
        link: '/productos/retail/mostaza-dulce-pe',
      },
      {
        name: 'Mayonesa Intercasa',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/mayonesa-pe',
      },
    ],
  },
  {
    id: 8,
    title: {
      es: 'Pechuga de pollo a la mostaza cremosa',
      en: 'Chicken breast in creamy mustard sauce',
      fr: 'Blanc de poulet à la moutarde crémeuse',
      pt: 'Peito de frango com molho cremoso de mostarda',
    },
    description: {
      es: 'Un plato elegante y fácil para cualquier día. La mostaza Intercasa crea una salsa suave.',
      en: 'An elegant, easy dish for any day. Intercasa mustard makes a smooth sauce.',
      fr: 'Un plat élégant et facile pour tous les jours. La moutarde Intercasa donne une sauce onctueuse.',
      pt: 'Um prato elegante e fácil para qualquer dia. A mostarda Intercasa cria um molho suave.',
    },
    date: '2025-03-22',
    slug: 'pechuga-pollo-mostaza-cremosa',
    imageUrl: '/images/recipes/pollo-mostaza.webp',
    imageAlt: {
      es: 'Pechuga de pollo a la mostaza cremosa Intercasa',
      en: 'Chicken breast in creamy mustard sauce with Intercasa',
      fr: 'Blanc de poulet à la moutarde crémeuse Intercasa',
      pt: 'Peito de frango com molho cremoso de mostarda Intercasa',
    },
    url: '/recetas/pechuga-pollo-mostaza-cremosa/',
    category: {
      es: 'Principales',
      en: 'Main courses',
      fr: 'Plats principaux',
      pt: 'Pratos principais',
    },
    introduction: {
      es: 'Un plato elegante y fácil para cualquier día. La mostaza Intercasa crea una salsa suave y llena de sabor.',
      en: 'An elegant, easy dish for any day. Intercasa mustard makes a smooth, flavour-packed sauce.',
      fr: 'Un plat élégant et facile pour tous les jours. La moutarde Intercasa donne une sauce onctueuse et pleine de saveur.',
      pt: 'Um prato elegante e fácil para qualquer dia. A mostarda Intercasa cria um molho suave e cheio de sabor.',
    },
    ingredients: {
      es: [
        '2 pechugas',
        '1 taza crema o leche evaporada',
        '2 cdas mostaza Intercasa',
        'Mantequilla, sal y pimienta',
      ],
      en: [
        '2 chicken breasts',
        '1 cup cream or evaporated milk',
        '2 tbsp Intercasa mustard',
        'Butter, salt and pepper',
      ],
      fr: [
        '2 blancs de poulet',
        '1 tasse de crème ou de lait évaporé',
        '2 c. à s. de moutarde Intercasa',
        'Beurre, sel et poivre',
      ],
      pt: [
        '2 peitos de frango',
        '1 chávena de natas ou leite evaporado',
        '2 colheres de sopa de mostarda Intercasa',
        'Manteiga, sal e pimenta',
      ],
    },
    instructions: {
      es: [
        'Dorar el pollo en una sartén.',
        'Cuando esté bien dorado, agregar la mantequilla, la crema y mostaza.',
        'Remover bien hasta integrarlo todo.',
        'Cocinar 10 minutos a fuego medio bajo.',
        'Servir con pasta o arroz.',
      ],
      en: [
        'Brown the chicken in a frying pan.',
        'Once nicely golden, add the butter, cream and mustard.',
        'Stir well until everything is combined.',
        'Cook for 10 minutes over a medium-low heat.',
        'Serve with pasta or rice.',
      ],
      fr: [
        'Faire dorer le poulet dans une poêle.',
        'Lorsqu’il est bien doré, ajouter le beurre, la crème et la moutarde.',
        'Bien remuer jusqu’à obtenir un mélange homogène.',
        'Laisser cuire 10 minutes à feu moyen-doux.',
        'Servir avec des pâtes ou du riz.',
      ],
      pt: [
        'Dourar o frango numa frigideira.',
        'Quando estiver bem dourado, juntar a manteiga, as natas e a mostarda.',
        'Mexer bem até ficar tudo bem misturado.',
        'Cozinhar durante 10 minutos em lume médio-brando.',
        'Servir com massa ou arroz.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Mostaza Dulce Intercasa',
        category: {
          es: 'Mostaza',
          en: 'Mustard',
          fr: 'Moutarde',
          pt: 'Mostarda',
        },
        link: '/productos/retail/mostaza-dulce-pe',
      },
    ],
  },
  {
    id: 9,
    title: {
      es: 'Sándwich de jamón y mostaza',
      en: 'Ham and mustard sandwich',
      fr: 'Sandwich au jambon et à la moutarde',
      pt: 'Sandes de fiambre e mostarda',
    },
    description: {
      es: 'Un clásico sencillo y delicioso. La mostaza Intercasa potencia el sabor del jamón.',
      en: 'A simple, delicious classic. Intercasa mustard enhances the flavour of the ham.',
      fr: 'Un classique simple et délicieux. La moutarde Intercasa relève la saveur du jambon.',
      pt: 'Um clássico simples e delicioso. A mostarda Intercasa realça o sabor do fiambre.',
    },
    date: '2025-05-10',
    slug: 'sandwich-jamon-mostaza',
    imageUrl: '/images/recipes/sandwich-jamon.webp',
    imageAlt: {
      es: 'Sándwich de jamón y mostaza Intercasa',
      en: 'Ham and mustard sandwich with Intercasa',
      fr: 'Sandwich au jambon et à la moutarde Intercasa',
      pt: 'Sandes de fiambre e mostarda Intercasa',
    },
    url: '/recetas/sandwich-jamon-mostaza/',
    category: {
      es: 'Snacks',
      en: 'Snacks',
      fr: 'En-cas',
      pt: 'Snacks',
    },
    introduction: {
      es: 'Un clásico sencillo y delicioso. La mostaza Intercasa potencia el sabor del jamón sin dominarlo.',
      en: 'A simple, delicious classic. Intercasa mustard enhances the flavour of the ham without overpowering it.',
      fr: 'Un classique simple et délicieux. La moutarde Intercasa relève la saveur du jambon sans la dominer.',
      pt: 'Um clássico simples e delicioso. A mostarda Intercasa realça o sabor do fiambre sem o dominar.',
    },
    ingredients: {
      es: [
        'Pan',
        'Jamón',
        'Queso',
        'Mostaza Intercasa',
        'Mantequilla',
      ],
      en: [
        'Bread',
        'Ham',
        'Cheese',
        'Intercasa mustard',
        'Butter',
      ],
      fr: [
        'Pain',
        'Jambon',
        'Fromage',
        'Moutarde Intercasa',
        'Beurre',
      ],
      pt: [
        'Pão',
        'Fiambre',
        'Queijo',
        'Mostarda Intercasa',
        'Manteiga',
      ],
    },
    instructions: {
      es: [
        'Untar mantequilla y mostaza en el pan.',
        'Añadir jamón y queso.',
        'Cerrar el sándwich.',
        'Dorar en sartén.',
        'Servir caliente.',
      ],
      en: [
        'Spread butter and mustard on the bread.',
        'Add the ham and cheese.',
        'Close the sandwich.',
        'Toast in a frying pan.',
        'Serve hot.',
      ],
      fr: [
        'Tartiner le pain de beurre et de moutarde.',
        'Ajouter le jambon et le fromage.',
        'Refermer le sandwich.',
        'Faire dorer à la poêle.',
        'Servir chaud.',
      ],
      pt: [
        'Untar o pão com manteiga e mostarda.',
        'Acrescentar o fiambre e o queijo.',
        'Fechar a sandes.',
        'Dourar numa frigideira.',
        'Servir quente.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Mostaza Dulce Intercasa',
        category: {
          es: 'Mostaza',
          en: 'Mustard',
          fr: 'Moutarde',
          pt: 'Mostarda',
        },
        link: '/productos/retail/mostaza-dulce-pe',
      },
    ],
  },

  // ==========================================
  // RECETAS CON ALIOLI INTERCASA
  // ==========================================
  {
    id: 10,
    title: {
      es: 'Papas bravas canarias con alioli',
      en: 'Canarian papas bravas with alioli',
      fr: 'Papas bravas canariennes à l’alioli',
      pt: 'Papas bravas canárias com alioli',
    },
    description: {
      es: 'Una tapa clásica española muy fácil de preparar. El alioli Intercasa aporta cremosidad.',
      en: 'A classic Spanish tapa that is very easy to make. Intercasa alioli adds creaminess.',
      fr: 'Une tapa classique espagnole très facile à préparer. L’alioli Intercasa apporte de l’onctuosité.',
      pt: 'Uma tapa clássica espanhola muito fácil de preparar. O alioli Intercasa dá cremosidade.',
    },
    date: '2025-07-18',
    slug: 'papas-bravas-canarias-alioli',
    imageUrl: '/images/recipes/papas-bravas.webp',
    imageAlt: {
      es: 'Papas bravas canarias con alioli Intercasa',
      en: 'Canarian papas bravas (spicy potatoes) with Intercasa alioli',
      fr: 'Papas bravas canariennes (pommes de terre épicées) à l’alioli Intercasa',
      pt: 'Papas bravas canárias (batatas picantes) com alioli Intercasa',
    },
    url: '/recetas/papas-bravas-canarias-alioli/',
    category: {
      es: 'Tapas',
      en: 'Tapas',
      fr: 'Tapas',
      pt: 'Tapas',
    },
    introduction: {
      es: 'Una tapa clásica española muy fácil de preparar. El alioli Intercasa aporta cremosidad y sabor intenso a cualquier tipo de papa.',
      en: 'A classic Spanish tapa that is very easy to make. Intercasa alioli, a garlic sauce in the Canarian style, adds creaminess and intense flavour to any type of potato.',
      fr: 'Une tapa classique espagnole très facile à préparer. L’alioli Intercasa, une sauce à l’ail à la canarienne, apporte de l’onctuosité et une saveur intense à tous les types de pommes de terre.',
      pt: 'Uma tapa clássica espanhola muito fácil de preparar. O alioli Intercasa, um molho de alho à moda canária, dá cremosidade e um sabor intenso a qualquer tipo de batata.',
    },
    ingredients: {
      es: [
        '4 papas',
        'Aceite',
        'Sal y paprika',
        'Alioli Intercasa',
      ],
      en: [
        '4 potatoes',
        'Oil',
        'Salt and paprika',
        'Intercasa alioli',
      ],
      fr: [
        '4 pommes de terre',
        'Huile',
        'Sel et paprika',
        'Alioli Intercasa',
      ],
      pt: [
        '4 batatas',
        'Azeite',
        'Sal e paprica',
        'Alioli Intercasa',
      ],
    },
    instructions: {
      es: [
        'Cortar las papas en cubitos medianos.',
        'Meter las papas en agua con hielo y dejarlas estar durante unos cuantos minutos.',
        'Secar muy bien las papas con papel de cocina.',
        'Freír en aceite de oliva, en la airfrier u hornear a 220° hasta que queden doradas y crujientes.',
        'Preparar alioli Intercasa.',
        'Servir con salsa encima.',
        'Añadir ketchup picante Intercasa si se desea más sabor.',
        'Cerrar con un poco de perejil picado.',
      ],
      en: [
        'Cut the potatoes into medium-sized cubes.',
        'Put the potatoes in ice-cold water and leave them for a few minutes.',
        'Dry the potatoes very well with kitchen paper.',
        'Fry in olive oil, in the air fryer, or bake at 220°C until golden and crispy.',
        'Prepare the Intercasa alioli.',
        'Serve with the sauce on top.',
        'Add Intercasa spicy ketchup if you would like more flavour.',
        'Finish with a little chopped parsley.',
      ],
      fr: [
        'Couper les pommes de terre en dés moyens.',
        'Plonger les pommes de terre dans de l’eau glacée et les laisser tremper quelques minutes.',
        'Bien les sécher avec du papier absorbant.',
        'Faire frire dans de l’huile d’olive, à la friteuse à air chaud ou cuire au four à 220 °C jusqu’à ce qu’elles soient dorées et croustillantes.',
        'Préparer l’alioli Intercasa.',
        'Servir avec la sauce par-dessus.',
        'Ajouter du ketchup piquant Intercasa si vous souhaitez plus de saveur.',
        'Terminer avec un peu de persil haché.',
      ],
      pt: [
        'Cortar as batatas em cubos médios.',
        'Colocar as batatas em água com gelo e deixá-las repousar durante alguns minutos.',
        'Secar muito bem as batatas com papel de cozinha.',
        'Fritar em azeite, na air fryer ou assar a 220°C até ficarem douradas e crocantes.',
        'Preparar o alioli Intercasa.',
        'Servir com o molho por cima.',
        'Acrescentar ketchup picante Intercasa se desejar mais sabor.',
        'Finalizar com um pouco de salsa picada.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Alioli Receta Canaria',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/alioli-receta-canaria-pe',
      },
      {
        name: 'Ketchup Picante Intercasa',
        category: {
          es: 'Ketchup',
          en: 'Ketchup',
          fr: 'Ketchup',
          pt: 'Ketchup',
        },
        link: '/productos/retail/ketchup-picante-cristal',
      },
    ],
  },
  {
    id: 11,
    title: {
      es: 'Sándwich de pollo con alioli',
      en: 'Chicken sandwich with alioli',
      fr: 'Sandwich au poulet et à l’alioli',
      pt: 'Sandes de frango com alioli',
    },
    description: {
      es: 'Una opción fresca y rápida para almuerzos. El alioli Intercasa aporta el toque cremoso.',
      en: 'A fresh, quick option for lunch. Intercasa alioli adds a creamy touch.',
      fr: 'Une option fraîche et rapide pour le déjeuner. L’alioli Intercasa apporte une touche onctueuse.',
      pt: 'Uma opção fresca e rápida para o almoço. O alioli Intercasa dá um toque cremoso.',
    },
    date: '2025-09-25',
    slug: 'sandwich-pollo-alioli',
    imageUrl: '/images/recipes/sandwich-pollo.webp',
    imageAlt: {
      es: 'Sándwich de pollo con alioli Intercasa',
      en: 'Chicken sandwich with Intercasa alioli',
      fr: 'Sandwich au poulet et à l’alioli Intercasa',
      pt: 'Sandes de frango com alioli Intercasa',
    },
    url: '/recetas/sandwich-pollo-alioli/',
    category: {
      es: 'Snacks',
      en: 'Snacks',
      fr: 'En-cas',
      pt: 'Snacks',
    },
    introduction: {
      es: 'Una opción fresca y rápida para almuerzos. El alioli Intercasa aporta el toque cremoso sin necesidad de más salsas.',
      en: 'A fresh, quick option for lunch. Intercasa alioli adds a creamy touch with no need for any other sauces.',
      fr: 'Une option fraîche et rapide pour le déjeuner. L’alioli Intercasa apporte une touche onctueuse, sans avoir besoin d’autres sauces.',
      pt: 'Uma opção fresca e rápida para o almoço. O alioli Intercasa dá um toque cremoso sem necessidade de mais molhos.',
    },
    ingredients: {
      es: [
        'Pechuga a la plancha',
        'Pan',
        'Alioli Intercasa',
        'Lechuga y tomate',
      ],
      en: [
        'Grilled chicken breast',
        'Bread',
        'Intercasa alioli',
        'Lettuce and tomato',
      ],
      fr: [
        'Blanc de poulet grillé',
        'Pain',
        'Alioli Intercasa',
        'Laitue et tomate',
      ],
      pt: [
        'Peito de frango grelhado',
        'Pão',
        'Alioli Intercasa',
        'Alface e tomate',
      ],
    },
    instructions: {
      es: [
        'Untar alioli Intercasa en el pan.',
        'Añadir el pollo.',
        'Incorporar vegetales.',
        'Cerrar y presionar.',
        'Servir.',
      ],
      en: [
        'Spread Intercasa alioli on the bread.',
        'Add the chicken.',
        'Add the vegetables.',
        'Close and press down.',
        'Serve.',
      ],
      fr: [
        'Tartiner le pain d’alioli Intercasa.',
        'Ajouter le poulet.',
        'Ajouter les légumes.',
        'Refermer et presser.',
        'Servir.',
      ],
      pt: [
        'Untar o pão com alioli Intercasa.',
        'Acrescentar o frango.',
        'Juntar os legumes.',
        'Fechar e pressionar.',
        'Servir.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Alioli Receta Canaria',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/alioli-receta-canaria-pe',
      },
    ],
  },
  {
    id: 12,
    title: {
      es: 'Pasta fría con alioli y vegetales',
      en: 'Cold pasta salad with alioli and vegetables',
      fr: 'Salade de pâtes froides à l’alioli et aux légumes',
      pt: 'Massa fria com alioli e legumes',
    },
    description: {
      es: 'Una ensalada fresca ideal para días calurosos. El alioli Intercasa da textura cremosa.',
      en: 'A fresh salad, ideal for hot days. Intercasa alioli gives it a creamy texture.',
      fr: 'Une salade fraîche, idéale pour les jours de chaleur. L’alioli Intercasa lui donne une texture onctueuse.',
      pt: 'Uma salada fresca, ideal para dias de calor. O alioli Intercasa dá uma textura cremosa.',
    },
    date: '2025-11-08',
    slug: 'pasta-fria-alioli-vegetales',
    imageUrl: '/images/recipes/pasta-fria.webp',
    imageAlt: {
      es: 'Pasta fría con alioli y vegetales Intercasa',
      en: 'Cold pasta salad with alioli and vegetables, made with Intercasa',
      fr: 'Salade de pâtes froides à l’alioli et aux légumes Intercasa',
      pt: 'Massa fria com alioli e legumes Intercasa',
    },
    url: '/recetas/pasta-fria-alioli-vegetales/',
    category: {
      es: 'Ensaladas',
      en: 'Salads',
      fr: 'Salades',
      pt: 'Saladas',
    },
    introduction: {
      es: 'Una ensalada fresca ideal para días calurosos. El alioli Intercasa le da una textura cremosa sin necesidad de más aderezos.',
      en: 'A fresh salad, ideal for hot days. Intercasa alioli gives it a creamy texture with no need for any other dressings.',
      fr: 'Une salade fraîche, idéale pour les jours de chaleur. L’alioli Intercasa lui donne une texture onctueuse, sans avoir besoin d’autres assaisonnements.',
      pt: 'Uma salada fresca, ideal para dias de calor. O alioli Intercasa dá-lhe uma textura cremosa sem necessidade de mais temperos.',
    },
    ingredients: {
      es: [
        '250 g pasta corta',
        'Maíz',
        'Jamón o pollo',
        'Pepino, pimiento (rojo, verde, amarillo), tomate, aceitunas negras',
        'Alioli Intercasa',
      ],
      en: [
        '250 g short pasta',
        'Sweetcorn',
        'Ham or chicken',
        'Cucumber, pepper (red, green, yellow), tomato, black olives',
        'Intercasa alioli',
      ],
      fr: [
        '250 g de pâtes courtes',
        'Maïs',
        'Jambon ou poulet',
        'Concombre, poivron (rouge, vert, jaune), tomate, olives noires',
        'Alioli Intercasa',
      ],
      pt: [
        '250 g de massa curta',
        'Milho',
        'Fiambre ou frango',
        'Pepino, pimento (vermelho, verde, amarelo), tomate, azeitonas pretas',
        'Alioli Intercasa',
      ],
    },
    instructions: {
      es: [
        'Cocer la pasta.',
        'Mezclar con los vegetales.',
        'Añadir alioli Intercasa.',
        'Sazonar.',
        'Refrigerar.',
      ],
      en: [
        'Cook the pasta.',
        'Mix with the vegetables.',
        'Add the Intercasa alioli.',
        'Season.',
        'Chill.',
      ],
      fr: [
        'Cuire les pâtes.',
        'Mélanger avec les légumes.',
        'Ajouter l’alioli Intercasa.',
        'Assaisonner.',
        'Réfrigérer.',
      ],
      pt: [
        'Cozer a massa.',
        'Misturar com os legumes.',
        'Juntar o alioli Intercasa.',
        'Temperar.',
        'Refrigerar.',
      ],
    },
    intercasaProducts: [
      {
        name: 'Alioli Receta Canaria',
        category: {
          es: 'Salsas',
          en: 'Sauces',
          fr: 'Sauces',
          pt: 'Molhos',
        },
        link: '/productos/retail/alioli-receta-canaria-pe',
      },
    ],
  },
];

/**
 * Obtener todas las recetas
 * @returns {Array} Array de objetos de recetas
 */
export const getAllRecipes = () => {
  return recipesData;
};

/**
 * Obtener una receta por su slug
 * @param {string} slug - Slug de la receta
 * @returns {Object|null} Objeto de receta o null si no se encuentra
 */
export const getRecipeBySlug = (slug) => {
  return recipesData.find(recipe => recipe.slug === slug) || null;
};

/**
 * Obtener una receta por su ID
 * @param {number} id - ID de la receta
 * @returns {Object|null} Objeto de receta o null si no se encuentra
 */
export const getRecipeById = (id) => {
  return recipesData.find(recipe => recipe.id === id) || null;
};

/**
 * Obtener recetas filtradas por tipo de producto
 * Busca recetas que contengan productos de la categoría especificada
 * @param {string} productType - Tipo de producto (ketchup, alioli, mayonesa, barbacoa, mostaza, tomate)
 * @returns {Array} Array de recetas que usan ese tipo de producto
 */
export const getRecipesByProductType = (productType) => {
  if (!productType) return recipesData;
  
  const productTypeLower = productType.toLowerCase();
  
  return recipesData.filter(recipe => {
    return recipe.intercasaProducts?.some(product => {
      // La búsqueda se hace siempre sobre el texto en español (idioma base),
      // independientemente del idioma que se esté mostrando.
      const productName = product.name?.toLowerCase() || '';
      const productCategory = (product.category?.es || product.category || '').toLowerCase();
      
      // Mapeo de tipos de producto a términos de búsqueda
      switch (productTypeLower) {
        case 'ketchup':
          return productCategory.includes('ketchup') || productName.includes('ketchup');
        case 'alioli':
          return productName.includes('alioli');
        case 'mayonesa':
          return productName.includes('mayonesa');
        case 'barbacoa':
          return productName.includes('barbacoa');
        case 'mostaza':
          return productCategory.includes('mostaza') || productName.includes('mostaza');
        case 'tomate':
        case 'tomate-frito':
        case 'tomates':
          return productCategory.includes('tomate') || productName.includes('tomate');
        default:
          return false;
      }
    });
  });
};

/**
 * Determina el tipo de producto basándose en el ID del producto
 * @param {string} productId - ID del producto retail
 * @returns {string|null} Tipo de producto para filtrar recetas
 */
export const getProductTypeFromId = (productId) => {
  if (!productId) return null;
  
  const idLower = productId.toLowerCase();
  
  if (idLower.includes('ketchup')) return 'ketchup';
  if (idLower.includes('alioli')) return 'alioli';
  if (idLower.includes('mayonesa')) return 'mayonesa';
  if (idLower.includes('barbacoa')) return 'barbacoa';
  if (idLower.includes('mostaza')) return 'mostaza';
  if (idLower.includes('tomate')) return 'tomate';
  
  return null;
};
